import React, { ChangeEvent, DragEvent, useRef, useState } from "react";
import Text from "@/components/ui/text";
import { CloudUpload, Loader, UploadFailed } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/utils/utils";

export type FileDropZoneState = "default" | "uploading" | "error";

/** Large is the standalone drag-and-drop area, and the affordance the edit
 *  variant of the field opens on; Small is the compact upload button the
 *  preview variant renders inline. Both accept a drop — the Small one just has
 *  no room to say so. */
export type FileDropZoneSize = "large" | "small";

/** Already-translated copy for every state of the zone. */
export interface FileDropZoneLabels {
  /** Large-size prompt before the browse link, e.g. "Drag and drop or". */
  dragAndDrop: string;
  browseFiles: string;
  uploading: string;
  uploadFailed: string;
  tryAgain: string;
}

interface Props {
  onFiles: (files: File[]) => void;
  labels: FileDropZoneLabels;
  state?: FileDropZoneState;
  /** Message rendered under the frame in the error state. */
  errorMessage?: string;
  /** Accepted formats and size, shown as helper text under the prompt. */
  description?: React.ReactNode;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  disabledTooltipText?: string;
  size?: FileDropZoneSize;
  /** Small-size label, e.g. "Upload file". Ignored at Large size, which spells
   *  the prompt out across the frame instead. */
  label?: string;
  dataTest?: string;
}

export const FileDropZone: React.FC<Props> = ({
  onFiles,
  labels,
  state = "default",
  errorMessage,
  description,
  accept,
  multiple = false,
  disabled = false,
  disabledTooltipText,
  size = "large",
  label,
  dataTest
}) => {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Nested children fire dragleave as the pointer crosses them, so the depth
  // is counted rather than toggled.
  const dragCounter = useRef<number>(0);

  const isUploading = state === "uploading";
  const isError = state === "error";
  const isSmall = size === "small";

  const handleBrowseClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const selectedFiles = event.target.files
      ? Array.from(event.target.files)
      : [];
    // The list is copied above, so clearing the input here is safe — and it is
    // what makes "Try again" work: an unchanged input value suppresses `change`,
    // so re-picking the file that just failed would never reach this handler.
    event.target.value = "";
    if (selectedFiles.length > 0) {
      onFiles(selectedFiles);
    }
  };

  const handleDrag = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragIn = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounter.current++;
    // Text and links dragged across the page also raise drag events; only a
    // file payload should light the frame up.
    if (Array.from(event.dataTransfer.types).includes("Files")) {
      setIsDragging(true);
    }
  };

  const handleDragOut = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    if (disabled) return;
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(event.dataTransfer.files);
      // Every dropped file is forwarded even in single-file mode: the owner
      // applies the limit and reports what it rejected, which a silent
      // truncation here would erase.
      onFiles(droppedFiles);
      event.dataTransfer.clearData();
    }
  };

  const renderCtaButton = (label: string) => (
    <Text
      as="button"
      type="button"
      className={cn(
        "p-1 rounded-lg transition-colors duration-200 ease-in-out bg-transparent border-none",
        // Disabled is not a dimmed default: the design gives it its own text
        // token and no CTA colour, so the prompt reads as one grey sentence
        // instead of a green link someone might still try to click.
        disabled ? "text-disabled" : "text-cta cursor-pointer hover:bg-surface-hover-strong"
      )}
      onClick={handleBrowseClick}
      disabled={disabled}
      data-test={dataTest ? `${dataTest}-browse` : undefined}
    >
      {label}
    </Text>
  );

  const dropHandlers = {
    onDragEnter: disabled ? undefined : handleDragIn,
    onDragLeave: disabled ? undefined : handleDragOut,
    onDragOver: disabled ? undefined : handleDrag,
    onDrop: handleDrop
  };

  const fileInput = (
    <input
      type="file"
      ref={fileInputRef}
      onChange={handleFileSelect}
      className="hidden"
      multiple={multiple}
      accept={accept}
    />
  );

  // Small says "Upload failed." below the frame, because its chip has room for
  // the recovery action and nothing else. Large spells the failure out inside
  // the frame, so the line below carries only the detail.
  const errorDetail = !isError
    ? undefined
    : isSmall
      ? [`${labels.uploadFailed}.`, errorMessage]
          .filter(Boolean)
          .join(" ")
      : errorMessage;

  const stateGlyph = isUploading ? (
    <Loader />
  ) : isError ? (
    <UploadFailed />
  ) : (
    <CloudUpload />
  );

  const stateIconColor = disabled
    ? "disabled"
    : isUploading
      ? "primary"
      : isError
        ? "error"
        : "secondary";

  // The compact variant is a button first and a drop target second: it sits in
  // a record row where the dashed frame has nowhere to go, so the affordance
  // is the label and the cloud glyph. The frame still lights up on drag so a
  // file dragged over the row has somewhere visible to land.
  const smallFrame = (
    <button
      type="button"
      onClick={handleBrowseClick}
      disabled={disabled}
      // The visible text is the state, not the resting label, so the
      // accessible name has to move with it — otherwise a screen reader
      // announces "Upload file" for a button that reads "Upload failed".
      aria-label={
        isUploading
          ? labels.uploading
          : isError
            ? `${labels.uploadFailed}. ${labels.tryAgain}`
            : label
      }
      className={cn(
        "w-fit max-w-full flex items-center gap-[6px] rounded-lg py-1 px-[6px] border-[0.5px] transition-colors duration-200 ease-in-out",
        isDragging
          ? "border-dashed border-border-drop-zone-active bg-surface-file-item-drag"
          : isError
            ? "border-border-field-error-strong bg-surface-field-default"
            : "border-border-badge-chip-default bg-surface-file-item",
        disabled
          ? "cursor-default opacity-50"
          : "cursor-pointer hover:bg-surface-hover-strong"
      )}
      data-test={dataTest}
      {...dropHandlers}
    >
      <IconContainer color={stateIconColor} className="flex-shrink-0">
        {stateGlyph}
      </IconContainer>
      <Text
        as="span"
        variant="body"
        className="truncate min-w-0"
        // The failure is never announced from here: Small always renders a
        // detail line below, which owns the alert even when the caller supplies
        // no message of its own. Taking the role here as well would announce
        // one failure twice.
        role={isUploading ? "status" : undefined}
      >
        {isUploading
          ? labels.uploading
          : isError
            ? labels.tryAgain
            : label}
      </Text>
    </button>
  );

  const frame = (
    <div
      className={cn(
        "relative w-full min-w-[240px] max-w-[720px] rounded-lg p-5 text-center transition-colors duration-200 flex items-center justify-center",
        // Disabled is checked first: it is a state of its own in the design,
        // with its own fill, border and text, and its border goes solid — a
        // dashed frame is the invitation to drop, which is exactly what a
        // disabled zone must not extend.
        disabled
          ? "border border-solid border-surface-disabled bg-surface-fields-disabled text-disabled cursor-not-allowed"
          : isDragging
            ? // The drag-over fill is a translucent layer over the resting fill
              // rather than a replacement, so both alphas composite the way the
              // design does.
              "border-2 border-dashed border-border-drop-zone-active bg-[linear-gradient(0deg,var(--white-8),var(--white-8)),linear-gradient(0deg,var(--white-2),var(--white-2))]"
            : isUploading
              ? "border border-solid border-border-border1 bg-surface-focus-selected"
              : isError
                ? "border border-solid border-border-field-error-strong bg-surface-field-default"
                : "border-[1.5px] border-dashed border-border-border1 bg-surface-field-default"
      )}
      onDragEnter={disabled ? undefined : handleDragIn}
      onDragLeave={disabled ? undefined : handleDragOut}
      onDragOver={disabled ? undefined : handleDrag}
      onDrop={handleDrop}
      data-test={dataTest}
    >
      <div className="flex flex-col items-center justify-center gap-1">
        <IconContainer
          size="md"
          color={stateIconColor}
          className="[&>svg]:size-full"
        >
          {stateGlyph}
        </IconContainer>
        {isUploading ? (
          <Text variant="body" role="status">
            {labels.uploading}
          </Text>
        ) : isError ? (
          // The detail message below is the fuller announcement and owns the
          // live region when it is present; without one, this line is all the
          // failure the user gets, so it takes the role instead. Two alerts
          // would announce the same failure twice.
          <Text variant="body" role={errorDetail ? undefined : "alert"}>
            {`${labels.uploadFailed}. `}
            {renderCtaButton(labels.tryAgain)}
          </Text>
        ) : (
          <Text variant="body">
            {`${labels.dragAndDrop} `}
            {renderCtaButton(labels.browseFiles)}
          </Text>
        )}
        {description && !isUploading && (
          <Text variant="smBodySecondary">{description}</Text>
        )}
      </div>
    </div>
  );

  const activeFrame = isSmall ? smallFrame : frame;

  return (
    <div className="flex flex-col gap-1">
      {/* A sibling of the frame, never a child: the compact variant's frame is
          a real button, and HTML forbids interactive content inside one. The
          browse click reaches it through the ref either way. */}
      {fileInput}
      {disabled && disabledTooltipText ? (
        <Tooltip>
          {/* A disabled button emits no pointer events, so the trigger would
              never see the hover that opens the tooltip. The Large frame is a
              plain div and needs no wrapper. */}
          <TooltipTrigger asChild>
            {isSmall ? <span className="w-fit">{activeFrame}</span> : activeFrame}
          </TooltipTrigger>
          <TooltipContent>{disabledTooltipText}</TooltipContent>
        </Tooltip>
      ) : (
        activeFrame
      )}
      {isSmall && description && !isUploading ? (
        <Text className="pl-1" variant="smBodySecondary">
          {description}
        </Text>
      ) : null}
      {errorDetail ? (
        // Announced rather than merely rendered: the failure is the result of
        // an action the user took, and a screen reader user has no reason to
        // go looking under the frame for it.
        <Text className="pl-1" variant="smBodyError" role="alert">
          {errorDetail}
        </Text>
      ) : null}
    </div>
  );
};
